/**
 * libwildsearch for application-specific usage
 * Manages state for keywords, search data, and results
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import type { WildSearchRecord } from '@n20a/libmiscfn';

export interface WildSearchResult extends WildSearchRecord {
  matchScore: number;
}

export interface IWildsearchContext {
  keywordsArray: string[] | null;
  setKeywordsArray: (keywords: string[] | null) => void;
  
  wildsearchArray: WildSearchRecord[] | null;
  setWildsearchArray: (data: WildSearchRecord[] | null) => void;
  
  wildsearchResultsArray: WildSearchResult[] | null;
  setWildsearchResultsArray: (results: WildSearchResult[] | null) => void;
  
  dataLoadTime: number | null;
  setDataLoadTime: (time: number | null) => void;
  
  searchTime: number | null;
  setSearchTime: (time: number | null) => void;
}

const WildsearchContext = createContext<IWildsearchContext | undefined>(undefined);

export const WildsearchProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [keywordsArray, setKeywordsArrayState] = useState<string[] | null>(null);
  const [wildsearchArray, setWildsearchArrayState] = useState<WildSearchRecord[] | null>(null);
  const [wildsearchResultsArray, setWildsearchResultsArrayState] = useState<WildSearchResult[] | null>(null);
  const [dataLoadTime, setDataLoadTimeState] = useState<number | null>(null);
  const [searchTime, setSearchTimeState] = useState<number | null>(null);

  // Wrapped setters with cleanup capability
  const setKeywordsArray = useCallback((keywords: string[] | null) => {
    setKeywordsArrayState(keywords);
  }, []);

  const setWildsearchArray = useCallback((data: WildSearchRecord[] | null) => {
    setWildsearchArrayState(data);
  }, []);

  const setWildsearchResultsArray = useCallback((results: WildSearchResult[] | null) => {
    setWildsearchResultsArrayState(results);
  }, []);

  const setDataLoadTime = useCallback((time: number | null) => {
    setDataLoadTimeState(time);
  }, []);

  const setSearchTime = useCallback((time: number | null) => {
    setSearchTimeState(time);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      setKeywordsArrayState(null);
      setWildsearchArrayState(null);
      setWildsearchResultsArrayState(null);
      setDataLoadTimeState(null);
      setSearchTimeState(null);
    };
  }, []);

  const value: IWildsearchContext = {
    keywordsArray,
    setKeywordsArray,

    wildsearchArray,
    setWildsearchArray,

    wildsearchResultsArray,
    setWildsearchResultsArray,

    dataLoadTime,
    setDataLoadTime,

    searchTime,
    setSearchTime
  };

  return (
    <WildsearchContext.Provider value={value}>
      {children}
    </WildsearchContext.Provider>
  );
};

export const useWildsearch = (): IWildsearchContext => {
  const context = useContext(WildsearchContext);
  if (context === undefined) {
    throw new Error('useWildsearch must be used within a WildsearchProvider');
  }
  return context;
};

export { WildsearchContext };
